import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, retry} from 'rxjs/operators';
import {HttpErrorHandler} from '../../http.error.handler';
import {environment} from '../../../environments/environment';
import {JsonApiResponse} from '../../shared/models/JsonApiResponse';
import { NewObservation, ObservationModel, ObservationTypeModel } from '../../shared/models/observation.model';
import {firstValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ObservationsService {
  constructor(private readonly http: HttpClient) {
  }

  getObservationTypes() {
    return firstValueFrom(
      this.http.get<JsonApiResponse<Array<ObservationTypeModel>>>(`${environment.services.service}v1/observation-type-group/all/type/list`)
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError), // then handle the error
          map(response => response.data)
        )
    );
  }

  getObservations() {
    const url = `${environment.services.service}v2/observation/active`;
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<ObservationModel[]>>(url)
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getObservationsByShiftId(shiftId: number) {
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<ObservationModel[]>>(`${environment.services.service}v1/shift/${shiftId}/observation/list`)
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  getObservationsByStormId(stormId: number) {
    return firstValueFrom(
      this.http
        .get<JsonApiResponse<ObservationModel[]>>(`${environment.services.service}v1/storm/${stormId}/observation/list`)
        .pipe(
          retry(3), // retry a failed request up to 3 times
          catchError(HttpErrorHandler.handleError) // then handle the error
        )
    );
  }

  postObservation(observation: NewObservation): Promise<ObservationModel> {
    const requestUrl = `${environment.services.service}v1/observation`;

    return firstValueFrom(
      this.http
        .post<JsonApiResponse<ObservationModel>>(requestUrl, observation)
        .pipe(
          catchError(HttpErrorHandler.handleError),
          map(response => response.data),
        )
    );
  }

  updateObservation(id: number, observation: NewObservation): Promise<ObservationModel> {
    const requestUrl = `${environment.services.service}v1/observation/${id}`;

    return firstValueFrom(
      this.http
        .put<JsonApiResponse<ObservationModel>>(requestUrl, observation)
        .pipe(
          catchError(HttpErrorHandler.handleError),
          map(response => response.data),
        )
    );
  }

  deleteObservation(id: number): Promise<void> {
    const requestUrl = `${environment.services.service}v1/observation/${id}`;

    return firstValueFrom(
      this.http.delete<void>(requestUrl)
        .pipe(
          catchError(HttpErrorHandler.handleError)
        )
    );
  }
}
